import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { vendorAPI, VendorBasicInfo, FraudIndicator, RiskAnalysis, CompetitionAnalysis } from '../../services/api';

// Vendor Types
export interface VendorDetails {
  legal_business_name: string;
  uei_sam: string;
  cage_code: string;
  registration_status: string;
  registration_date: string;
  last_update_date?: string;
  expiration_date?: string;
  physical_address?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    stateOrProvinceCode: string;
    zipCode: string;
    zipCodePlus4?: string;
    countryCode?: string;
  };
  mailing_address?: any;
  entity_url?: string;
  primary_naics?: string;
  naics_list?: Array<{
    naicsCode: string;
    naicsDescription: string;
    sbaSmallBusiness: string;
  }>;
  business_types?: {
    businessTypeList: Array<{
      businessTypeCode: string;
      businessTypeDesc: string;
    }>;
  };
  congressional_district?: string;
}

export interface VendorAward {
  award_id: string;
  award_url?: string;
  date: string;
  amount: number;
  agency: string;
  office?: string;
  description: string;
  naics?: string;
  naics_description?: string;
  set_aside?: string;
  location_city: string;
  location_state: string;
  competition?: string;
  number_of_offers?: number;
  sole_source?: boolean;
}

// Unified backend response interface
export interface UnifiedRiskAnalysis {
  risk_score: number;
  risk_level: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  risk_indicators: FraudIndicator[];
  fraud_probability: number;
  investigation_priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  requires_investigation: boolean;
  // Backward compatibility fields
  total_risk_score?: number;
  risk_factors?: string[];
  fraud_risk_score?: number;
  fraud_risk_level?: string;
  fraud_indicators_count?: number;
}

export interface BackendRiskAnalysis {
  total_risk_score: number;
  risk_factors: string[];
  risk_level: string;
  fraud_risk_score: number;
  fraud_risk_level: string;
  fraud_indicators_count: number;
}

export interface BackendFraudAnalysis {
  vendor_uei: string;
  vendor_name: string;
  overall_risk_score: number;
  risk_level: string;
  total_indicators: number;
  indicators_by_severity: {
    CRITICAL: number;
    HIGH: number;
    MEDIUM: number;
    LOW: number;
  };
  fraud_indicators: FraudIndicator[];
  summary: string;
}

export interface VendorSummary {
  vendor_info: any;
  sam_totals: any;
  usaspending_totals: any;
  combined_totals: {
    award_count: number;
    total_amount: number;
    avg_award_amount: number;
    sole_source_count?: number;
    sole_source_percentage?: number;
    full_competition_count?: number;
    full_competition_percentage?: number;
  };
  // New unified risk analysis
  risk_analysis?: UnifiedRiskAnalysis;
  // Backward compatibility fields
  fraud_analysis?: BackendFraudAnalysis;
  competition_analysis?: CompetitionAnalysis;
  agency_diversity?: {
    unique_agencies: number;
    top_agencies: Array<{
      name: string;
      count: number;
      percentage: number;
    }>;
  };
  award_timeline?: Array<{
    year: string;
    award_count: number;
    total_amount: number;
  }>;
  award_count?: number;
  processing_time?: number;
  last_updated?: string;
}

export interface VendorSearchRequest {
  query: string;
  search_type?: 'name' | 'uei' | 'auto';
  filters?: {
    location?: string;
    naics?: string;
  };
  limit?: number;
  sort_by?: 'relevance' | 'award_count';
}

interface VendorState {
  // Search
  searchResults: VendorBasicInfo[];
  searchQuery: string;
  searchType: 'name' | 'uei' | 'auto';
  sortBy: 'relevance' | 'award_count';
  totalFound: number;
  
  // Selected vendor
  selectedVendor: VendorBasicInfo | null;
  vendorDetails: VendorDetails | null;
  vendorSummary: VendorSummary | null;
  
  // Awards
  samAwards: VendorAward[];
  usaspendingAwards: VendorAward[];
  awardsPage: number;
  awardsPerPage: number;
  totalAwardsPages: number;
  
  // UI State
  loading: boolean;
  detailModalOpen: boolean;
  error: string | null;
  
  // Totals
  samTotals: any | null;
  usaspendingTotals: any | null;
}

const initialState: VendorState = {
  searchResults: [],
  searchQuery: '',
  searchType: 'auto',
  sortBy: 'relevance',
  totalFound: 0,
  selectedVendor: null,
  vendorDetails: null,
  vendorSummary: null,
  samAwards: [],
  usaspendingAwards: [],
  awardsPage: 1,
  awardsPerPage: 50,
  totalAwardsPages: 0,
  loading: false,
  detailModalOpen: false,
  error: null,
  samTotals: null,
  usaspendingTotals: null,
};

// Async thunks
export const searchVendors = createAsyncThunk(
  'vendor/search',
  async (request: VendorSearchRequest, { rejectWithValue }) => {
    try {
      const response = await vendorAPI.searchVendors(request);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Search failed');
    }
  }
);

export const fetchVendorDetails = createAsyncThunk(
  'vendor/fetchDetails',
  async (uei: string, { rejectWithValue }) => {
    try {
      const [details, awards, summary] = await Promise.all([
        vendorAPI.getVendorDetails(uei),
        vendorAPI.getVendorAwards(uei),
        vendorAPI.getVendorSummary(uei)
      ]);
      return { details, awards, summary };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch vendor details');
    }
  }
);

export const fetchVendorAwards = createAsyncThunk(
  'vendor/fetchAwards',
  async ({ uei, source = 'all', page = 1, perPage = 50 }: { uei: string; source?: string; page?: number; perPage?: number }, { rejectWithValue }) => {
    try {
      const response = await vendorAPI.getVendorAwards(uei, source, page, perPage);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch awards');
    }
  }
);

const vendorSlice = createSlice({
  name: 'vendor',
  initialState,
  reducers: {
    setSearchType: (state, action: PayloadAction<'name' | 'uei' | 'auto'>) => {
      state.searchType = action.payload;
    },
    setSortBy: (state, action: PayloadAction<'relevance' | 'award_count'>) => {
      state.sortBy = action.payload;
    },
    setSelectedVendor: (state, action: PayloadAction<VendorBasicInfo>) => {
      state.selectedVendor = action.payload;
      state.detailModalOpen = true;
    },
    closeDetailModal: (state) => {
      state.detailModalOpen = false;
      state.selectedVendor = null;
      state.vendorDetails = null;
      state.vendorSummary = null;
      state.samAwards = [];
      state.usaspendingAwards = [];
      state.samTotals = null;
      state.usaspendingTotals = null;
    },
    clearSearch: (state) => {
      state.searchResults = [];
      state.searchQuery = '';
      state.totalFound = 0;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Search vendors
      .addCase(searchVendors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchVendors.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload.vendors || [];
        state.totalFound = action.payload.total_found || 0;
        state.searchQuery = action.payload.search_query || '';
      })
      .addCase(searchVendors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Search failed';
      })
      // Fetch vendor details
      .addCase(fetchVendorDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.vendorDetails = action.payload.details;
        state.samAwards = action.payload.awards.sam_awards || [];
        state.usaspendingAwards = action.payload.awards.usaspending_awards || [];
        state.samTotals = action.payload.awards.sam_totals || null;
        state.usaspendingTotals = action.payload.awards.usaspending_totals || null;
        state.vendorSummary = action.payload.summary;
        state.totalAwardsPages = action.payload.awards.total_pages || 1;
      })
      .addCase(fetchVendorDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch vendor details';
      })
      // Fetch vendor awards
      .addCase(fetchVendorAwards.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchVendorAwards.fulfilled, (state, action) => {
        state.loading = false;
        state.samAwards = action.payload.sam_awards || [];
        state.usaspendingAwards = action.payload.usaspending_awards || [];
        state.samTotals = action.payload.sam_totals || null;
        state.usaspendingTotals = action.payload.usaspending_totals || null;
        state.awardsPage = action.payload.page || 1;
        state.totalAwardsPages = action.payload.total_pages || 1;
      })
      .addCase(fetchVendorAwards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch awards';
      });
  }
});

export const { 
  setSearchType, 
  setSortBy,
  setSelectedVendor, 
  closeDetailModal, 
  clearSearch, 
  clearError 
} = vendorSlice.actions;

export default vendorSlice.reducer;